import { css } from '@emotion/react'
import { Button, Cell, DropdownItem, Grid, Heading, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { FieldStatusEnum, Form, FormRenderProps, ListFieldStatus, PecFormApi } from 'components/form'
import { HLabel } from 'components/HLabel'
import { TextStatus } from 'components/text/TextStatus'
import { AnyObject } from 'final-form'
import { useState } from 'react'
import { noop } from 'util/functions'
import { metaPath } from 'util/metaPath'
import { getEditableItemRemoveTooltipText } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import { EditableRow } from 'view/atendimentos/detail/components/EditableList'
import { Observacao } from 'view/atendimentos/detail/components/Observacao'
import { ObservacaoFormGrid } from 'view/atendimentos/detail/components/ObservacaoFormGrid'
import { removeWithOptionalConfirm } from 'view/retificacao-atendimento/removeWithOptionalConfirm'

import { IntervencaoPlanoItemModel } from '../model-plano'
import { intervencaoPlanoItemValidator } from '../validator-plano'

const meta = metaPath<IntervencaoPlanoItemModel>()

interface IntervencaoPlanoRowProps<T extends IntervencaoPlanoItemModel> {
  model: T
  label: string
  getDescricao(model: T): string
  getCodigo(model: T): string
  onChange(values: T): void
  onRemove(itemToRemove: T): void
  onReset?(itemToReset: T): void
  status?: FieldStatusEnum
  controlModifiedStatus?: boolean
}

export function IntervencaoPlanoRow<T extends IntervencaoPlanoItemModel>(props: Readonly<IntervencaoPlanoRowProps<T>>) {
  const {
    model,
    label,
    getDescricao,
    getCodigo,
    onChange,
    onRemove,
    onReset = noop,
    status = FieldStatusEnum.NAO_MODIFICADO,
    controlModifiedStatus = false,
  } = props
  const [editing, setEditing] = useState(false)
  const descricao = getDescricao(model)

  const toggleEditing = () => setEditing((oldState) => !oldState)

  const handleRemove = () =>
    removeWithOptionalConfirm({
      shouldAskConfirmation: controlModifiedStatus,
      title: `Deseja excluir o campo ${label}?`,
      icon: { name: 'exclamationTriangleOutline', fill: 'alert' },
      onRemove: () => onRemove(model),
    })

  const handleSubmit = (value: T, formApi: PecFormApi<T>) => {
    onChange(value)
    setTimeout(formApi.reset)
    toggleEditing()
  }

  const renderForm = (formRenderProps: FormRenderProps<T>) => (
    <VFlow vSpacing={0.5} style={styles.observacaoFormWrapper}>
      <Heading level={4}>Adicionar observação</Heading>
      <HLabel title={`${label}:`}>{descricao}</HLabel>
      <ObservacaoFormGrid
        name={meta.observacao}
        onCancel={toggleEditing}
        onSubmit={formRenderProps.handleSubmit}
        dataTestId='observacoesIntervencaoRowPlano'
      />
    </VFlow>
  )

  return (
    <EditableRow editing={editing} dataTestId='intervencaoPlanoRow'>
      {!editing && (
        <>
          <Grid style={styles.grid}>
            <Cell size={9} alignSelf='center'>
              <TextStatus status={status}>{`${descricao} - ${getCodigo(model)}`}</TextStatus>
            </Cell>
            <Cell size={3} alignSelf='center' style={styles.actionsWrapper}>
              <IntervencaoPlanoRowActions
                model={model}
                status={status}
                controlModifiedStatus={controlModifiedStatus}
                onEdit={toggleEditing}
                onRemove={handleRemove}
                onReset={() => onReset(model)}
              />
            </Cell>
          </Grid>
          <Observacao observacao={model.observacao} status={status} />
        </>
      )}
      {editing && (
        <Form<T>
          render={renderForm}
          initialValues={model}
          onSubmit={handleSubmit}
          validate={intervencaoPlanoItemValidator}
          initialValuesEqual={initialValuesEqual}
        />
      )}
    </EditableRow>
  )
}

interface IntervencaoPlanoRowActionsProps {
  model: IntervencaoPlanoItemModel
  status: FieldStatusEnum
  controlModifiedStatus: boolean
  onEdit(): void
  onRemove(): void
  onReset(): void
}

function IntervencaoPlanoRowActions(props: Readonly<IntervencaoPlanoRowActionsProps>) {
  const { model, status, controlModifiedStatus, onEdit, onRemove, onReset } = props
  const { isEditable, tooltipText: removeTooltipText } = getEditableItemRemoveTooltipText(model)
  const editTooltipText = model.observacao ? 'Editar observação' : 'Adicionar observação'

  if (controlModifiedStatus) {
    return (
      <ListFieldStatus status={status} onReset={onReset} onRemove={onRemove}>
        <DropdownItem onClick={onEdit}>
          <HFlow alignItems='center' hSpacing={0.5}>
            <Text variant='h4'>{editTooltipText}</Text>
          </HFlow>
        </DropdownItem>
      </ListFieldStatus>
    )
  }

  return (
    <>
      <Tooltip text={editTooltipText}>
        <Button size='small' skin='ghost' onClick={onEdit} data-testid='adicionarObservacaoIntervencaoPlano'>
          <Icon icon='chatOutline' />
        </Button>
      </Tooltip>
      <Tooltip text={removeTooltipText}>
        <Button size='small' skin='ghost' onClick={onRemove} disabled={isEditable}>
          <Icon icon='trashOutline' />
        </Button>
      </Tooltip>
    </>
  )
}

const initialValuesEqual = (oldInitial: AnyObject | undefined, newInitial: AnyObject | undefined) =>
  oldInitial?._id === newInitial?._id

const styles = {
  actionsWrapper: css`
    padding: 0.25rem 0;
    text-align: right;
  `,
  observacaoFormWrapper: css`
    padding: 1rem 0;
  `,
  grid: css`
    margin: 0;
  `,
}
